import styled from '@emotion/styled';
import { Repeat, Trash2, CreditCard } from 'lucide-react';
import { Accordion } from '@template-project/afrodita-components';
import type { Order } from '@/domain/entities/order.entity';
import { canModifyOrder } from './utils';

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const OptionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme, disabled }) => (disabled ? theme.colors.text.tertiary : theme.colors.text.primary)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.15s;
  font-family: inherit;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

const HeaderText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
`;

interface ModifyOrderAccordionProps {
  readonly order: Order;
  readonly onNavigateToReturns?: () => void;
}

export function ModifyOrderAccordion({ order, onNavigateToReturns }: Readonly<ModifyOrderAccordionProps>) {
  const modifiable = canModifyOrder(order);

  return (
    <Accordion
      variant="box"
      header={<HeaderText>Modify My Order</HeaderText>}
      disabled={!modifiable}
      content={
        <OptionList>
          <OptionButton disabled={!modifiable}>
            <Repeat size={18} />
            Change Size &amp; Color
          </OptionButton>
          <OptionButton disabled={!modifiable}>
            <Trash2 size={18} />
            Remove Items
          </OptionButton>
          <OptionButton onClick={onNavigateToReturns}>
            <CreditCard size={18} />
            Billing Inquiry / Dispute
          </OptionButton>
        </OptionList>
      }
    />
  );
}
